/**
 * Reference stopwords — merchandising words that leak into Shopify-derived
 * reference numbers.
 *
 * Shopify has no reference field, so the importer derives one from
 * `variant.sku` (falling back to the product handle). Brands routinely bake
 * store-state words into those strings — `W-BEL-GYS-PREORDER`,
 * `goofy-panda-coming-soon` — and the word then becomes part of the ref's
 * identity: it shows in the app, it feeds `referenceNormalized`, and it
 * survives long after the product stopped being a pre-order.
 *
 * These words are parasitic: they describe the listing, not the watch. The
 * curated list is stripped from the derived string BEFORE `normalizeReference`
 * runs, so both `reference` and `referenceNormalized` land clean on the same
 * import.
 *
 * Pure module by design (no Firebase SDK import) so the matching rules are
 * unit-testable — same split as siblingReferences.ts. The live, admin-editable
 * list is read/written by importStopwordsConfig.ts.
 */

/**
 * The seed list, also the fallback whenever the config doc is missing.
 *
 * Every entry is matched case-insensitively and gated on alphanumeric word
 * boundaries, so spelling variants (`preorder` vs `pre-order` vs `pre order`)
 * each need their own entry — that is deliberate: a fuzzy, separator-insensitive
 * match would also strip `pre` out of `presale`.
 */
export const DEFAULT_IMPORT_STOPWORDS: string[] = [
  'preorder',
  'pre-order',
  'pre order',
  'coming soon',
  'comingsoon',
  'to be launched',
  'to-be-launched',
  'tobelaunched',
  'new arrival',
  'new-arrival',
  'sold out',
  'soldout',
  'sold-out',
  'limited stock',
  'low stock',
  'sale',
  'on-sale',
  'on sale',
];

/**
 * Trim, lowercase, collapse internal whitespace and dedupe a stopword list.
 * Applied on read and before every save so the stored doc and the in-memory
 * list always agree.
 */
export function normalizeStopwordList(raw: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const t = entry.trim().replace(/\s+/g, ' ').toLowerCase();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Separators that can be left stranded once a stopword is cut out of the middle
// of a SKU or a handle slug (`W-BEL-PREORDER-GYS` → `W-BEL--GYS`).
const SEPARATORS = '-–—_/.';
const SEPARATOR_RUN = new RegExp(`[${escapeRegExp(SEPARATORS)}]{2,}`, 'g');
const EDGE_JUNK = new RegExp(
  `^[\\s${escapeRegExp(SEPARATORS)}]+|[\\s${escapeRegExp(SEPARATORS)}]+$`,
  'g',
);

/**
 * THE matcher — the single place a stopword is recognised, shared by the
 * reference strip and the readable-text strip below. Only the tidy-up that
 * follows differs between the two; what counts as a stopword never does.
 *
 * Matching is case-insensitive and gated on alphanumeric boundaries — `sale`
 * does NOT match inside `WHOLESALE` or `PRESALE`. Punctuation counts as a
 * boundary, which is what makes the SKU/handle case work: the `-` either side
 * of `PREORDER` in `W-BEL-PREORDER-GYS` is a boundary, so the word is cut.
 *
 * Admin-entered values are treated as literals, never as regex source.
 *
 * Returns the string with every match excised and NOTHING else changed, so the
 * caller can compare against the input to detect "no stopword was present".
 */
function cutStopwords(raw: string, stopwords: string[]): string {
  let out = raw;
  for (const stopword of stopwords) {
    const t = (stopword ?? '').trim();
    if (!t) continue;
    const pattern = new RegExp(
      `(?<![A-Za-z0-9])${escapeRegExp(t)}(?![A-Za-z0-9])`,
      'gi',
    );
    out = out.replace(pattern, '');
  }
  return out;
}

/**
 * Strip every stopword from a derived reference string.
 *
 * Uses the shared matcher, then heals the SKU/handle debris the cut leaves
 * behind: `W-BEL-PREORDER-GYS` → `W-BEL--GYS` → `W-BEL-GYS`.
 *
 * SAFETY: if stripping would leave nothing `normalizeReference` could key on
 * (no letters or digits at all), the ORIGINAL string is returned untouched — a
 * product literally called `PRE-ORDER` keeps its junk ref rather than losing its
 * identity entirely. Readable text takes the opposite view; see
 * `stripImportStopwordsFromText`.
 */
export function stripImportStopwords(raw: string, stopwords: string[]): string {
  if (!raw) return raw;
  if (!stopwords || stopwords.length === 0) return raw;

  const cut = cutStopwords(raw, stopwords);
  if (cut === raw) return raw;

  const out = cut
    // Brackets left empty by the word that used to sit inside them
    // ("AM1-B-1 (SOLD OUT)" → "AM1-B-1 ()").
    .replace(/\(\s*\)|\[\s*\]|\{\s*\}/g, '')
    // …and whitespace stranded just inside a bracket that still holds content.
    .replace(/([([{])\s+/g, '$1')
    .replace(/\s+([)\]}])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    // A run of separators can only be junk we created — collapse to the first.
    .replace(SEPARATOR_RUN, (match) => match[0])
    // " - " / "- " style debris left where a whole word used to sit.
    .replace(/\s+([-–—_/.])/g, '$1')
    .replace(/([-–—_/.])\s+/g, '$1')
    .replace(EDGE_JUNK, '')
    .trim();

  // Nothing identity-bearing survived → keep the original.
  if (!/[A-Za-z0-9]/.test(out)) return raw;
  return out;
}

// Separators that read as punctuation in a human-facing name — the ones a cut
// word can leave stranded ("Summer Series - Purple Haze - Preorder"). `.` is
// deliberately absent from the collapse class (it belongs to "5.11", "Mk.II")
// but present in the edge trim, where a dangling full stop is only debris.
const TEXT_SEPARATORS = '-–—_/|,;:';
const TEXT_SEPARATOR_RUN = new RegExp(
  `([${escapeRegExp(TEXT_SEPARATORS)}])(?:\\s*[${escapeRegExp(TEXT_SEPARATORS)}])+`,
  'g',
);
const TEXT_EDGE_JUNK = new RegExp(
  `^[\\s${escapeRegExp(TEXT_SEPARATORS)}.]+|[\\s${escapeRegExp(TEXT_SEPARATORS)}.]+$`,
  'g',
);

/**
 * Strip every stopword from a human-readable Shopify-derived string — nickname,
 * model, product name, series, collection.
 *
 * Same matcher as `stripImportStopwords` (deliberately — one curated list, one
 * definition of a stopword). Two things differ, both about what happens AFTER
 * the cut:
 *
 * 1. Tidy-up preserves prose spacing. The reference path collapses ` - ` to `-`
 *    because a SKU has no spaces to protect; doing that to
 *    "Summer Series - Purple Haze - Preorder" would yield
 *    "Summer Series-Purple Haze". Here the trailing separator is trimmed and an
 *    orphaned interior one collapsed, but the surviving " - " is left alone.
 * 2. An empty result is ALLOWED and returned as ''. A ref must keep its
 *    identity at all costs; a nickname that was only ever "Preorder" is pure
 *    merchandising noise and is better absent — callers drop the field rather
 *    than write a bare stopword.
 */
export function stripImportStopwordsFromText(raw: string, stopwords: string[]): string {
  if (!raw) return raw;
  if (!stopwords || stopwords.length === 0) return raw;

  const cut = cutStopwords(raw, stopwords);
  if (cut === raw) return raw;

  return cut
    // Brackets left empty by the word that used to sit inside them
    // ("Purple Haze (Sold Out)" → "Purple Haze ()").
    .replace(/\(\s*\)|\[\s*\]|\{\s*\}/g, '')
    // Two separators now adjacent can only be the gap a cut word left between
    // them ("Summer Series -  - Purple Haze").
    .replace(TEXT_SEPARATOR_RUN, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    // …and the same debris stranded just inside a bracket that still has content
    // ("Purple Haze (Preorder, Blue)" → "Purple Haze (, Blue)").
    .replace(new RegExp(`([([{])[\\s${escapeRegExp(TEXT_SEPARATORS)}]+`, 'g'), '$1')
    .replace(new RegExp(`[\\s${escapeRegExp(TEXT_SEPARATORS)}]+([)\\]}])`, 'g'), '$1')
    .replace(TEXT_EDGE_JUNK, '')
    .trim();
}
